import {
  useCommunicationHistory,
  useGetCallEntryById,
  useGetCommunicationHistory,
} from "@features/cx/shared/hooks/useCommunication";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Mail, MessageCircle, MessageSquare, Phone } from "lucide-react";
import { useState } from "react";

interface CommunicationsProps {
  type: string;
  remoteUtilityId: number;
  entityId: string;
  action: string;
}
// Helper functions
const getChannelColor = (channel: string) => {
  switch (channel) {
    case "EMAIL":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "PHONE":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "SMS":
    case "WHATSAPP":
      return "bg-green-100 text-green-800 border-green-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getChannelIcon = (channel: string) => {
  switch (channel?.toUpperCase()) {
    case "EMAIL":
      return <Mail className="h-4 w-4" />;
    case "PHONE":
      return <Phone className="h-4 w-4" />;
    case "SMS":
    case "WHATSAPP":
      return <MessageCircle className="h-4 w-4" />;
    default:
      return <MessageSquare className="h-4 w-4" />;
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const formatTime = (dateString: string) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export function RequestCommunicationsLandingTab({
  type,
  remoteUtilityId,
  entityId,
  action,
}: CommunicationsProps) {
  console.log(remoteUtilityId, entityId);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const { data: callEntry } = useGetCallEntryById(entityId);

  const { data: communications } = useGetCommunicationHistory(
    String(remoteUtilityId),
    entityId
  );

  const handleAccordionChange = (value: string[]) => {
    setOpenItems(value);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          {communications?.results?.length > 0 ? (
            <Accordion
              type="multiple"
              value={openItems}
              onValueChange={handleAccordionChange}
            >
              {communications?.results?.map((comm) => (
                <AccordionItem key={comm.id} value={comm.id.toString()}>
                  <AccordionTrigger className="py-4 px-1">
                    <div className="flex items-center gap-3 text-left">
                      <Badge className={getChannelColor(comm.channelType)}>
                        <span className="flex items-center gap-1">
                          {getChannelIcon(comm.channelTypeDisplay)}
                          {comm.channelType?.charAt(0).toUpperCase() +
                            comm.channelType?.slice(1)}
                        </span>
                      </Badge>
                      <div className="min-w-28 md:min-w-36">
                        <div className="text-sm font-medium">
                          {formatDate(comm.createdDate)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTime(comm.createdDate)}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {comm.message?.subject || "No Subject"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {comm.message?.to?.length > 0
                            ? "To Customer"
                            : "From Customer"}
                        </div>
                      </div>
                      <Badge variant="outline" className="hidden md:flex">
                        {comm.statusDisplay || "NA"}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <div className="p-3 bg-muted rounded-md text-sm">
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              comm.message?.body || "No content available",
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="font-medium">Status</div>
                          <div>{comm.statusDisplay || "NA"}</div>
                        </div>

                        <div>
                          <div className="font-medium">
                            {comm.message?.to?.length > 0
                              ? "Sent By"
                              : "Handled By"}
                          </div>
                          <div>
                            {comm.template?.createdUserRemoteName || "NA"}
                          </div>
                        </div>

                        {comm.runTime && (
                          <div>
                            <div className="font-medium">Run Time</div>
                            <div>{comm.runTime}</div>
                          </div>
                        )}
                      </div>

                      {/* <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm">View Full Details</Button>
                        {comm.channelType === "EMAIL" && (
                          <Button size="sm" className="flex items-center gap-1" onClick={onSendMessage}>
                            <Send className="h-4 w-4" />
                            Reply
                          </Button>
                        )}
                      </div> */}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-6">
              No communication history available.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
